import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserCog, Plus, Archive, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TeacherAssignments() {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [years, setYears] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fTeacher, setFTeacher] = useState("all");
  const [fGroup, setFGroup] = useState("all");
  const [form, setForm] = useState({ teacher_id: "", subject_id: "", group_id: "", school_year_id: "" });
  const [busy, setBusy] = useState(false);

  const loadRefs = async () => {
    try {
      const [t, s, g, y] = await Promise.all([
        api.get("/people/teachers", { params: { active_only: true } }).catch(() => ({ data: [] })),
        api.get("/subjects").catch(() => ({ data: [] })),
        api.get("/school/groups").catch(() => ({ data: [] })),
        api.get("/school-years").catch(() => ({ data: [] })),
      ]);
      setTeachers(t.data || []); setSubjects(s.data || []); setGroups(g.data || []); setYears(y.data || []);
      const active = (y.data || []).find((x) => x.is_active) || (y.data || []).find((x) => x.active) || (y.data || [])[0];
      if (active) setForm((f) => ({ ...f, school_year_id: active.id || active._id }));
    } catch (e) { /* noop */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const q = [];
      if (fTeacher !== "all") q.push(`teacher_id=${fTeacher}`);
      if (fGroup !== "all") q.push(`group_id=${fGroup}`);
      const { data } = await api.get(`/school/teacher-assignments${q.length ? "?" + q.join("&") : ""}`);
      setRows(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fTeacher, fGroup]);

  const gid = (o) => o.id || o._id;
  const save = async () => {
    if (!form.teacher_id || !form.group_id) { toast.error("Professeur et groupe requis."); return; }
    if (rows.some((r) => r.teacher_id === form.teacher_id && r.subject_id === form.subject_id && r.group_id === form.group_id && r.status === "Actif")) {
      toast.error("Cette affectation active existe déjà."); return;
    }
    setBusy(true);
    try {
      await api.post("/school/teacher-assignments", { ...form, status: "Actif" });
      toast.success("Affectation créée."); setForm((f) => ({ ...f, teacher_id: "", subject_id: "", group_id: "" })); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  const end = async (id) => { try { await api.put(`/school/teacher-assignments/${id}/end`); toast.success("Affectation archivée."); await load(); } catch (e) { toast.error("Erreur"); } };

  return (
    <div data-testid="teacher-assignments-page">
      <div className="flex items-center gap-2 mb-1"><UserCog className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-display font-bold text-slate-900">Affectations professeurs</h1></div>
      <p className="text-sm text-slate-500 mb-6">Associez un professeur à une matière et un groupe interne. Ces affectations alimentent le planning hebdomadaire et Pédagogie.</p>

      <Card className="p-4 border-slate-200 mb-5">
        <div className="grid md:grid-cols-5 gap-2 items-end">
          <div><label className="text-xs text-slate-500">Professeur</label>
            <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
              <SelectTrigger data-testid="ta-teacher"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{teachers.map((t) => <SelectItem key={gid(t)} value={gid(t)}>{t.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">Matière</label>
            <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="ta-subject"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={gid(s)} value={gid(s)}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">Groupe interne</label>
            <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
              <SelectTrigger data-testid="ta-group"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={gid(g)} value={gid(g)}>{g.group_name || g.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">Année scolaire</label>
            <Select value={form.school_year_id} onValueChange={(v) => setForm({ ...form, school_year_id: v })}>
              <SelectTrigger data-testid="ta-year"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={gid(y)} value={gid(y)}>{y.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button onClick={save} disabled={busy} className="gap-2 rounded-full" data-testid="ta-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Affecter</Button>
        </div>
      </Card>

      <div className="flex gap-2 mb-3">
        <Select value={fTeacher} onValueChange={setFTeacher}><SelectTrigger className="w-52" data-testid="ta-filter-teacher"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les professeurs</SelectItem>{teachers.map((t) => <SelectItem key={gid(t)} value={gid(t)}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fGroup} onValueChange={setFGroup}><SelectTrigger className="w-52" data-testid="ta-filter-group"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les groupes</SelectItem>{groups.map((g) => <SelectItem key={gid(g)} value={gid(g)}>{g.group_name || g.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70"><TableHead>Professeur</TableHead><TableHead>Matière</TableHead><TableHead>Groupe</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Aucune affectation.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.id} data-testid="ta-row">
                    <TableCell className="font-medium">{r.teacher_name}</TableCell>
                    <TableCell>{r.subject_name}</TableCell>
                    <TableCell>{r.group_name}</TableCell>
                    <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                    <TableCell className="text-right">{r.status === "Actif" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => end(r.id)}><Archive className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
